/**
 * 客戶表單(REQ-20260419009 補齊欄位 + 排版重構)。
 *
 * - 17 個 Customer 主檔欄位 + phones[](3 phone + 3 fax)全部曝露於 UI
 * - 行業類別 / 收款憑證 / 收款方式 三個 select 接 useReferences()
 * - 桌機 3 欄密集排版,對齊舊系統 ref/index.html 佈局
 * - mobile 為 Customer 本體欄位(獨立),不進 phones[]
 * - 業務專員(ownerId)後端自動帶入,本表單不曝露切換 UI
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type SubmitHandler } from 'react-hook-form';
import {
  CreateCustomerSchema,
  PhoneType,
  type CreateCustomerInput,
  type CustomerPhone,
  type CustomerResponse,
} from '@app/shared';
import { AddressInput } from '../../components/AddressInput';
import { fetchNextCustomerCode } from './api';
import { AddressMapButton, WebsiteOpenButton } from '../../components/LinkOutButtons';
import { useReferences } from '../settings/api';

export interface CustomerFormHandle {
  submit: () => void;
  reset: () => void;
  isDirty: () => boolean;
}

export interface CustomerFormProps {
  defaultValues?: Partial<CustomerResponse> | undefined;
  ownerId: number;
  readOnly?: boolean | undefined;
  onSubmit: (values: CreateCustomerInput) => void;
  onDirtyChange?: ((dirty: boolean) => void) | undefined;
  /** 後端 409 指名的重複欄位(REQ-20260803001):該欄位加紅框 + 欄位下紅字 */
  conflictField?: 'cCode' | 'tax' | null | undefined;
  /** 搭配 conflictField 顯示在該欄位下方的訊息 */
  conflictMessage?: string | null | undefined;
  /**
   * REQ-20260912001 / REQ-20260912002:「新增」與「有寫入權的編輯」模式都打開 ——
   * 使用者 key 完客戶編號前 3 碼分類碼後,自動向後端要同前綴的下一個 6 碼流水號
   * 補成 9 碼(客戶常是先接洽建檔、確定接案才回頭補編號)。由父層依模式 / 權限傳入
   * (對齊 REQ-20260905005 SupplierForm 的 autoFocusCode 做法,不在表單內部反推模式)。
   */
  autoSuggestCode?: boolean | undefined;
}

/**
 * 從 phones[] 抽出指定類型的三槽,以 displayOrder 對位(允許槽位有空缺)。
 * 例:儲存時跳過 displayOrder=1 → 載入時槽 1 也為空。
 */
function extractSlots(phones: CustomerPhone[] | undefined, type: PhoneType): string[] {
  const out = ['', '', ''];
  for (const p of phones ?? []) {
    if (p.phoneType !== type) continue;
    if (p.displayOrder >= 0 && p.displayOrder < 3) out[p.displayOrder] = p.number;
  }
  return out;
}

/** 把 6 個槽位字串 + mobile 整理成送 API 的 phones[]。 */
function buildPhonesPayload(phoneSlots: string[], faxSlots: string[]): CustomerPhone[] {
  const out: CustomerPhone[] = [];
  phoneSlots.forEach((number, i) => {
    const trimmed = number.trim();
    if (trimmed) out.push({ phoneType: PhoneType.PHONE, displayOrder: i, number: trimmed });
  });
  faxSlots.forEach((number, i) => {
    const trimmed = number.trim();
    if (trimmed) out.push({ phoneType: PhoneType.FAX, displayOrder: i, number: trimmed });
  });
  return out;
}

function buildDefaults(
  defaultValues: Partial<CustomerResponse> | undefined,
  ownerId: number,
): CreateCustomerInput {
  return {
    cCode: defaultValues?.cCode ?? null,
    fullName: defaultValues?.fullName ?? '',
    simpleName: defaultValues?.simpleName ?? '',
    tax: defaultValues?.tax ?? null,
    industryId: defaultValues?.industryId ?? null,
    contact: defaultValues?.contact ?? null,
    position: defaultValues?.position ?? null,
    mobile: defaultValues?.mobile ?? null,
    email: defaultValues?.email ?? null,
    website: defaultValues?.website ?? null,
    address: defaultValues?.address ?? null,
    addressInvoice: defaultValues?.addressInvoice ?? null,
    paymentCertId: defaultValues?.paymentCertId ?? null,
    paymentMethodId: defaultValues?.paymentMethodId ?? null,
    notes: defaultValues?.notes ?? null,
    ownerId,
    phones: defaultValues?.phones ?? [],
  };
}

const nullIfEmpty = (v: unknown): string | null => {
  if (v == null) return null;
  const s = String(v);
  return s === '' ? null : s;
};
const nullableNumber = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * 表單內依 DOM 順序排好的輸入格(REQ-20260916004)。
 *
 * ⚠️ 刻意**不用** `offsetParent` / `checkVisibility` / `getBoundingClientRect` 過濾
 * 「看不看得見」—— jsdom 沒有版面,那幾個判斷一律 falsy,清單會被濾成 0 個,
 * 跳格靜默失效而測試照樣全綠。`disabled` 與 `type="hidden"` 是屬性判斷,
 * 在 jsdom 與瀏覽器裡結果一致,所以只濾這兩個。
 */
function formFields(form: HTMLFormElement): HTMLElement[] {
  const all = form.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >('input, select, textarea');
  return Array.from(all).filter(
    (el) => !el.disabled && !(el instanceof HTMLInputElement && el.type === 'hidden'),
  );
}

/**
 * 游標是否已經停在字串的最前 / 最後,而且沒有選取範圍
 * (REQ-20260904002 / REQ-20260910001 的同一條規則:游標在字中間或有選取範圍時
 * 方向鍵一律維持原生行為,否則使用者打錯字就再也移不回去改)。
 *
 * ⚠️ `type="email"` / `type="url"` / `type="number"` 的 input 讀 `selectionStart`
 * 會拋 `InvalidStateError`(Chrome)或回 `null`。取不到值時一律回 `false`
 * = **不跳格**(保守):寧可少跳一次,也不要在使用者還在改字時把焦點搬走。
 * 那幾格的 Enter 不受影響,照樣跳。刻意不為此把 type 改成 text —— 會失去瀏覽器驗證。
 */
function atCaretEdge(el: HTMLInputElement, edge: 'start' | 'end'): boolean {
  try {
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (start == null || end == null || start !== end) return false;
    return edge === 'start' ? start === 0 : start === el.value.length;
  } catch {
    return false;
  }
}

/**
 * 主檔表單的鍵盤跳格(REQ-20260916004)。掛在 `<form>` 上做事件代理,依 DOM 順序
 * 找下一 / 上一格 —— 這張是 20 多格的 12-col grid,照品項列那種「逐格配對 ref」
 * 的寫法(REQ-20260904002 / REQ-20260911001)會多出二十幾個 ref 與接線。
 *
 * 既有規則照抄,不重新論證:組字中的 Enter 是**選字**不是跳格;方向鍵只在游標
 * 已經到邊界時才跳;焦點延一幀才移(讓瀏覽器先把它那一份做完);**不加任何 tabIndex**。
 *
 * **Enter 一律 `preventDefault()`**:這張 `<form>` 內沒有任何 `type="submit"` 的按鈕,
 * 工具列的「儲存」走 `formRef.current.submit()`,所以 Enter 是目前唯一會觸發
 * implicit submission 的路徑 —— 需求就是要它不再存檔。最後一格按 Enter 停在原地。
 *
 * `<select>` 只攔 Enter:原生的 ← / → / ↑ / ↓ 是切換選項。
 * ↑ / ↓ 兩張表單都一鍵不攔 —— 格狀版面下「下一格」與「視覺上正下方那一格」
 * 不是同一件事,猜錯比留白貴。
 */
function focusSiblingOnKey(e: React.KeyboardEvent<HTMLFormElement>): void {
  if (e.nativeEvent.isComposing) return;
  const target = e.target;
  if (
    !(target instanceof HTMLInputElement) &&
    !(target instanceof HTMLSelectElement) &&
    !(target instanceof HTMLTextAreaElement)
  ) {
    return;
  }

  let step: 1 | -1;
  if (e.key === 'Enter') {
    // 備註是 textarea,那裡的 Enter 是換行
    if (target instanceof HTMLTextAreaElement) return;
    step = 1;
  } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
    if (!(target instanceof HTMLInputElement)) return;
    if (!atCaretEdge(target, e.key === 'ArrowRight' ? 'end' : 'start')) return;
    step = e.key === 'ArrowRight' ? 1 : -1;
  } else {
    return;
  }

  e.preventDefault();
  const fields = formFields(e.currentTarget);
  const idx = fields.indexOf(target);
  const next = idx < 0 ? undefined : fields[idx + step];
  if (next) requestAnimationFrame(() => next.focus());
}

export const CustomerForm = forwardRef<CustomerFormHandle, CustomerFormProps>(
  function CustomerForm(
    {
      defaultValues,
      ownerId,
      readOnly,
      onSubmit,
      onDirtyChange,
      conflictField,
      conflictMessage,
      autoSuggestCode,
    },
    ref,
  ) {
    const industries = useReferences('industries', false);
    const paymentCerts = useReferences('payment-certs', false);
    const paymentMethods = useReferences('payment-methods', false);

    const initialValues = useRef(buildDefaults(defaultValues, ownerId));
    const form = useForm<CreateCustomerInput>({
      resolver: zodResolver(CreateCustomerSchema),
      defaultValues: initialValues.current,
    });
    const {
      register,
      handleSubmit,
      reset,
      watch,
      setValue,
      getValues,
      formState: { errors, isDirty },
    } = form;

    // 電話 / 傳真各 3 槽。獨立 useState,送出時組進 phones[]。
    const initialPhoneSlots = useMemo(() => extractSlots(defaultValues?.phones, PhoneType.PHONE), [defaultValues]);
    const initialFaxSlots = useMemo(() => extractSlots(defaultValues?.phones, PhoneType.FAX), [defaultValues]);
    const [phoneSlots, setPhoneSlots] = useState<string[]>(initialPhoneSlots);
    const [faxSlots, setFaxSlots] = useState<string[]>(initialFaxSlots);
    const initialPhonesRef = useRef<{ phone: string[]; fax: string[] }>({
      phone: initialPhoneSlots,
      fax: initialFaxSlots,
    });
    const [slotsDirty, setSlotsDirty] = useState(false);

    // 切換客戶時把表單與 phone/fax 槽都重置。
    const idKey = defaultValues?.id ?? null;
    useEffect(() => {
      const next = buildDefaults(defaultValues, ownerId);
      initialValues.current = next;
      reset(next);
      const ph = extractSlots(defaultValues?.phones, PhoneType.PHONE);
      const fx = extractSlots(defaultValues?.phones, PhoneType.FAX);
      setPhoneSlots(ph);
      setFaxSlots(fx);
      initialPhonesRef.current = { phone: ph, fax: fx };
      setSlotsDirty(false);
    }, [idKey, ownerId, reset, defaultValues]);

    // REQ-20261001002:新開視窗 / 重新整理時客戶資料比下拉選項先到 —— register 把 id
    // 寫進 DOM 時還沒有那個 option,瀏覽器回落到「— 請選擇 —」,選項到了 RHF 也不會
    // 再寫一次。選項一到就把表單目前的值(含使用者改過的)重寫回 DOM;不帶 shouldDirty。
    useEffect(() => {
      for (const name of ['industryId', 'paymentCertId', 'paymentMethodId'] as const) {
        setValue(name, getValues(name));
      }
    }, [industries.data, paymentCerts.data, paymentMethods.data, setValue, getValues]);

    // 同步 dirty 狀態(RHF dirty 或 phone/fax 槽動過皆算 dirty)。
    useEffect(() => {
      onDirtyChange?.(isDirty || slotsDirty);
    }, [isDirty, slotsDirty, onDirtyChange]);

    useImperativeHandle(
      ref,
      (): CustomerFormHandle => ({
        submit: () => {
          void handleSubmit((values: CreateCustomerInput) => {
            onSubmit({ ...values, phones: buildPhonesPayload(phoneSlots, faxSlots) });
          })();
        },
        reset: () => {
          reset(initialValues.current);
          setPhoneSlots(initialPhonesRef.current.phone);
          setFaxSlots(initialPhonesRef.current.fax);
          setSlotsDirty(false);
        },
        isDirty: () => isDirty || slotsDirty,
      }),
      [handleSubmit, onSubmit, reset, isDirty, slotsDirty, phoneSlots, faxSlots],
    );

    const handle: SubmitHandler<CreateCustomerInput> = (values) => {
      onSubmit({ ...values, phones: buildPhonesPayload(phoneSlots, faxSlots) });
    };
    const disabled = !!readOnly;

    /** 該欄位就是後端指名的衝突欄位時,回傳要顯示在欄位下方的訊息。 */
    const conflictOf = (field: 'cCode' | 'tax'): string | undefined =>
      conflictField === field ? conflictMessage ?? undefined : undefined;

    // REQ-20260912001:客戶編號打滿前 3 碼分類碼後自動補後 6 碼流水號。
    const cCodeField = register('cCode', { setValueAs: nullIfEmpty });
    const cCodeInputRef = useRef<HTMLInputElement | null>(null);
    const suggestTimerRef = useRef<number | undefined>(undefined);
    useEffect(() => () => window.clearTimeout(suggestTimerRef.current), []);

    const handleCCodeChange = (e: ChangeEvent<HTMLInputElement>): void => {
      void cCodeField.onChange(e);
      if (autoSuggestCode !== true || disabled) return;

      window.clearTimeout(suggestTimerRef.current);
      const prefix = e.target.value.trim();
      if (!/^\d{3}$/.test(prefix)) return;
      // REQ-20260912002:打回這筆客戶載入時原本就有的編號 → 不取號。正式資料有
      // 2010 年代匯入的 3 碼短編號客戶(001 / 008 / 012),編輯這種客戶時只要在
      // 欄位裡動一下再改回原值,onChange 就會看到「剛好 3 位數字」而把人家的舊編號
      // 覆寫成 9 碼。新增模式 defaultValues 為 undefined,此條恆為 false。
      if (prefix === (defaultValues?.cCode ?? '').trim()) return;

      suggestTimerRef.current = window.setTimeout(() => {
        void fetchNextCustomerCode(prefix)
          .then((code) => {
            const input = cCodeInputRef.current;
            // stale guard:回應到達時欄位已經不是當初那 3 碼(使用者又打了字 / 清空 /
            // 換前綴)就丟棄 —— 受控欄位在打字途中被推導值覆寫會吃掉按鍵。
            if (code == null || !input || input.value.trim() !== prefix) return;
            setValue('cCode', code, { shouldDirty: true });
            input.setSelectionRange(code.length, code.length);
          })
          .catch(() => undefined);
      }, 250);
    };

    const setSlot = (
      kind: 'phone' | 'fax',
      idx: number,
      value: string,
    ): void => {
      const setter = kind === 'phone' ? setPhoneSlots : setFaxSlots;
      setter((prev) => {
        const next = [...prev];
        next[idx] = value;
        return next;
      });
      setSlotsDirty(true);
    };

    return (
      <form
        onSubmit={handleSubmit(handle)}
        onKeyDown={focusSiblingOnKey}
        className="grid gap-x-2 gap-y-2 md:grid-cols-12 text-sm bg-white border border-emerald-200 rounded p-3"
        data-testid="customer-form"
      >
        {/* Row 1 — 客戶編號 / 簡稱 / 統編 / 行業類別 / 收款憑證 / 收款方式(各 2 欄) */}
        <Field
          label="客戶編號"
          error={errors.cCode?.message ?? conflictOf('cCode')}
          colSpan={2}
        >
          <input
            type="text"
            {...cCodeField}
            ref={(el) => {
              cCodeField.ref(el);
              cCodeInputRef.current = el;
            }}
            onChange={handleCCodeChange}
            className={conflictField === 'cCode' ? inputConflictCls : inputCls}
            disabled={disabled}
          />
        </Field>
        <Field label="簡稱 *" error={errors.simpleName?.message} colSpan={2}>
          <input type="text" {...register('simpleName')} className={inputCls} disabled={disabled} />
        </Field>
        <Field
          label="統一編號"
          error={errors.tax?.message ?? conflictOf('tax')}
          colSpan={2}
        >
          <input
            type="text"
            {...register('tax', { setValueAs: nullIfEmpty })}
            className={conflictField === 'tax' ? inputConflictCls : inputCls}
            maxLength={8}
            disabled={disabled}
          />
        </Field>
        <Field label="行業類別" error={errors.industryId?.message} colSpan={2}>
          <select
            {...register('industryId', { setValueAs: nullableNumber })}
            className={inputCls}
            disabled={disabled || industries.isLoading}
          >
            <option value="">— 請選擇 —</option>
            {(industries.data?.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="收款憑證" error={errors.paymentCertId?.message} colSpan={2}>
          <select
            {...register('paymentCertId', { setValueAs: nullableNumber })}
            className={inputCls}
            disabled={disabled || paymentCerts.isLoading}
          >
            <option value="">— 請選擇 —</option>
            {(paymentCerts.data?.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="收款方式" error={errors.paymentMethodId?.message} colSpan={2}>
          <select
            {...register('paymentMethodId', { setValueAs: nullableNumber })}
            className={inputCls}
            disabled={disabled || paymentMethods.isLoading}
          >
            <option value="">— 請選擇 —</option>
            {(paymentMethods.data?.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>

        {/* Row 2 — 公司全名 / 網站(各 6 欄) */}
        <Field label="公司全名 *" error={errors.fullName?.message} colSpan={6}>
          <input type="text" {...register('fullName')} className={inputCls} disabled={disabled} />
        </Field>
        {/* 輸入格 + 開啟鈕是兩個控制項共用一個標籤 → 用 GroupField(理由見該元件註解) */}
        <GroupField label="網站" error={errors.website?.message} colSpan={6}>
          <div className="flex gap-1">
            <input
              type="url"
              {...register('website', { setValueAs: nullIfEmpty })}
              aria-label="網站"
              /* min-w-0 不可省:input 的預設最小寬(約 172px)會把按鈕擠出格子 */
              className={`${inputCls} min-w-0`}
              placeholder="https://example.com"
              disabled={disabled}
            />
            <WebsiteOpenButton value={watch('website')} />
          </div>
        </GroupField>

        {/* Row 3 — 聯絡人 / 職稱 / 行動 / Email */}
        <Field label="聯絡人" error={errors.contact?.message} colSpan={2}>
          <input
            type="text"
            {...register('contact', { setValueAs: nullIfEmpty })}
            className={inputCls}
            disabled={disabled}
          />
        </Field>
        <Field label="職稱" error={errors.position?.message} colSpan={2}>
          <input
            type="text"
            {...register('position', { setValueAs: nullIfEmpty })}
            className={inputCls}
            disabled={disabled}
          />
        </Field>
        <Field label="行動" error={errors.mobile?.message} colSpan={2}>
          <input
            type="text"
            {...register('mobile', { setValueAs: nullIfEmpty })}
            className={inputCls}
            disabled={disabled}
          />
        </Field>
        <Field label="Email" error={errors.email?.message} colSpan={6}>
          <input
            type="email"
            {...register('email', { setValueAs: nullIfEmpty })}
            className={inputCls}
            disabled={disabled}
          />
        </Field>

        {/* Row 4 — 公司地址 / 送貨地址(各 6 欄) */}
        <GroupField label="公司地址" error={errors.address?.message} colSpan={6}>
          <AddressInput
            label="公司地址"
            value={watch('address')}
            onChange={(next) => setValue('address', nullIfEmpty(next), { shouldDirty: true })}
            inputClassName={inputCls}
            disabled={disabled}
          >
            <AddressMapButton value={watch('address')} label="公司地址" />
          </AddressInput>
        </GroupField>
        <GroupField label="送貨地址" error={errors.addressInvoice?.message} colSpan={6}>
          <AddressInput
            label="送貨地址"
            value={watch('addressInvoice')}
            onChange={(next) => setValue('addressInvoice', nullIfEmpty(next), { shouldDirty: true })}
            inputClassName={inputCls}
            disabled={disabled}
          >
            <AddressMapButton value={watch('addressInvoice')} label="送貨地址" />
          </AddressInput>
        </GroupField>

        {/* Row 5 — 電話 ×3 + 傳真 ×3 同一排 */}
        <PhoneSlotGroup
          label="電話"
          values={phoneSlots}
          onChange={(i, v) => setSlot('phone', i, v)}
          disabled={disabled}
          testIdPrefix="customer-phone"
          colSpan={6}
        />
        <PhoneSlotGroup
          label="傳真"
          values={faxSlots}
          onChange={(i, v) => setSlot('fax', i, v)}
          disabled={disabled}
          testIdPrefix="customer-fax"
          colSpan={6}
        />

        {/* Row 6 — 備註 全寬 */}
        <Field label="備註" error={errors.notes?.message} colSpan={12}>
          <textarea
            {...register('notes', { setValueAs: nullIfEmpty })}
            className={inputCls}
            rows={3}
            disabled={disabled}
          />
        </Field>
      </form>
    );
  },
);

const inputCls =
  'w-full border border-slate-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 disabled:bg-slate-100 disabled:text-slate-500';

/**
 * 欄位級衝突(後端 409)的紅框樣式。與 inputCls 同結構,只換邊框 / focus 顏色 ——
 * 刻意不用 `${inputCls} border-red-400` 串接,因為 Tailwind 產出的 CSS 順序
 * 不保證後寫的 border-color 會勝出。
 */
const inputConflictCls =
  'w-full border border-red-400 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 disabled:bg-slate-100 disabled:text-slate-500';

/**
 * 標籤樣式 — 對齊舊系統 ref/css/style.css 的 .columeTitle(#9DFFB5 mint 綠底)。
 * 用 inline-block + 自身對齊 start,避免被 grid track 拉滿整列寬度。
 */
const labelCls = 'text-xs font-bold text-emerald-800';

/** Tailwind JIT 需要 static class names — 這個 map 列出所有用到的 col-span 值 */
const SPAN_CLS: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  6: 'md:col-span-6',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  12: 'md:col-span-12',
};

function Field(props: {
  label: string;
  error?: string | undefined;
  colSpan?: 1 | 2 | 3 | 4 | 6 | 8 | 9 | 12;
  children: React.ReactNode;
}): JSX.Element {
  const span = SPAN_CLS[props.colSpan ?? 1] ?? '';
  return (
    <label className={`grid gap-0.5 ${span}`}>
      <span className={labelCls}>{props.label}</span>
      {props.children}
      {props.error ? (
        <span role="alert" className="text-xs text-red-600">
          {props.error}
        </span>
      ) : null}
    </label>
  );
}

/**
 * 一個標籤配多個控制項的欄位列(REQ-20260901004 的地址列、REQ-20260902006 的網站列)。
 * 外觀與 `Field` 相同,但用 `div` + `span` 而不是包住 children 的 `<label>` ——
 * 郵遞區號 / 地址是兩個 input、網站是 input + 開啟鈕,包在同一個 label 裡會讓
 * `getByLabelText('公司地址')` 抓到多個元素(`<button>` 也是 labelable)。
 * 標籤關聯改由各控制項自己的 aria-label 承接,做法同 PhoneSlotGroup。
 */
function GroupField(props: {
  label: string;
  error?: string | undefined;
  colSpan?: 1 | 2 | 3 | 4 | 6 | 8 | 9 | 12;
  children: React.ReactNode;
}): JSX.Element {
  const span = SPAN_CLS[props.colSpan ?? 1] ?? '';
  return (
    <div className={`grid gap-0.5 ${span}`}>
      <span className={labelCls}>{props.label}</span>
      {props.children}
      {props.error ? (
        <span role="alert" className="text-xs text-red-600">
          {props.error}
        </span>
      ) : null}
    </div>
  );
}

function PhoneSlotGroup(props: {
  label: string;
  values: string[];
  onChange: (idx: number, value: string) => void;
  disabled: boolean;
  testIdPrefix: string;
  /** 此群組在外層 12-col grid 中佔幾欄(預設 12 = 全寬;6 = 半寬) */
  colSpan?: 6 | 12;
}): JSX.Element {
  const span = props.colSpan === 6 ? 'md:col-span-6' : 'md:col-span-12';
  return (
    <div className={`grid gap-0.5 ${span}`}>
      <span className={labelCls}>{props.label}</span>
      <div className="grid gap-1 md:grid-cols-3">
        {props.values.map((v, i) => (
          <input
            key={i}
            type="text"
            value={v}
            onChange={(e) => props.onChange(i, e.target.value)}
            placeholder={`${props.label} ${i + 1}`}
            className={inputCls}
            disabled={props.disabled}
            data-testid={`${props.testIdPrefix}-${i}`}
            aria-label={`${props.label} ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
